import React, { Dispatch, FC, SetStateAction, useMemo } from 'react'
import { Col, Container, Row } from 'components/layout'
import { Packet, PacketAddOn } from 'components'
import './enrollForm.scss'
import { ProductWithAddOns, SelectedPacket } from './enrollFormTypes'
import { sortHandler, constants } from 'helpers'
import { useTranslation } from 'react-i18next'
import { plusIcon } from 'assets/svg/svgs'
import { colors } from 'styles/colors'

type PacketSelectionBaseProps = {
    selectedProduct: ProductWithAddOns
    handlePacketSelection: Dispatch<SetStateAction<SelectedPacket[]>>
    preSelectedPackets?: SelectedPacket[]
}

type PacketSelectionInterface = PacketSelectionBaseProps & {
    isAddOn?: boolean
}

const convertProductPricesToSelectedPackets = (
    product: ProductWithAddOns
): SelectedPacket[] =>
    product?.product_prices?.map((productPrice) => ({
        pricebook_id: productPrice.pricebook.sf_id,
        product_id: product.sf_id,
        price: productPrice.price?.toString(),
        size: productPrice.package_size?.toString(),
        size_display: productPrice.package_size_display,
        name: product.name,
        hidePrice: product.hide_price,
        unit: product.unit,
        package_id: productPrice.sf_id,
        uuid: undefined,
        image_url: product.image_url,
        isAddOn: false
    })) ?? []

const convertAddOnsToSelectedPackets = (
    product: ProductWithAddOns
): SelectedPacket[] =>
    product?.addOns?.map((addon) => ({
        pricebook_id: addon.pricebook_id,
        product_id: addon.product_id,
        price: addon.price?.toString(),
        size: addon.size?.toString(),
        size_display: addon.size_display,
        name: addon.product_name,
        hidePrice: addon.hide_price,
        unit: addon.unit,
        package_id: addon.package_id,
        image_url: addon.image_url,
        uuid: undefined,
        isAddOn: true
    })) ?? []

const getPacketQuantity = (
    packet: SelectedPacket,
    preSelectedPackets?: SelectedPacket[]
): number => {
    let totalPreSelectedPackets = 0
    if (preSelectedPackets) {
        preSelectedPackets.forEach((preSelectedPacket) => {
            if (preSelectedPacket.package_id === packet.package_id) {
                totalPreSelectedPackets += 1
            }
        })
    }
    return totalPreSelectedPackets
}

const PacketSelectionProduct: FC<PacketSelectionBaseProps> = ({
    selectedProduct,
    handlePacketSelection,
    preSelectedPackets
}) => {
    const packetNameFormat = () => {
        const [name, subName] = selectedProduct?.name?.split('(') || []
        return {
            name: name?.trim() || '',
            subName: subName ? `(${subName?.trim()}` : ''
        }
    }

    const packetList = useMemo(
        () =>
            sortHandler(
                convertProductPricesToSelectedPackets(selectedProduct),
                'size',
                constants.SORT_TYPE.ALPHA_NUMERIC,
                constants.SORT_DIRECTION.DESCENDING
            ) as SelectedPacket[],
        [selectedProduct]
    )

    const headerStyle =
        selectedProduct?.image_url &&
        /\.(png|jpg|jpeg|svg)$/i.test(selectedProduct?.image_url)
            ? { backgroundImage: `url(${selectedProduct?.image_url})` }
            : {}

    return (
        <div data-testid="packet-selection" className="packet-selection">
            <div
                data-testid="packet-selection--section-header"
                className="packet-selection--section-header"
                style={headerStyle}
            />
            <div className="packet-selection--main">
                <div className="packet-selection--heading">
                    <h2 className="packet-selection--heading-title">
                        {packetNameFormat().name}
                    </h2>
                    <p className="packet-selection--heading-subtitle">
                        {packetNameFormat().subName}
                    </p>
                </div>
                <div>
                    <Container>
                        <Row>
                            {selectedProduct &&
                                packetList.map((productPacket) => (
                                    <Col
                                        md={3}
                                        col={6}
                                        key={productPacket.package_id}
                                    >
                                        <div className="packet-selection--single-packet">
                                            <Packet
                                                productPacket={productPacket}
                                                quantity={
                                                    getPacketQuantity(
                                                        productPacket,
                                                        preSelectedPackets
                                                    ) || 0
                                                }
                                                packetSelectionHandler={
                                                    handlePacketSelection
                                                }
                                            />
                                        </div>
                                    </Col>
                                ))}
                        </Row>
                    </Container>
                </div>
            </div>
        </div>
    )
}

const PacketSelectionAddOn: FC<PacketSelectionBaseProps> = ({
    selectedProduct,
    handlePacketSelection,
    preSelectedPackets
}) => {
    const { t } = useTranslation()

    const packetList = useMemo(
        () =>
            sortHandler(
                convertAddOnsToSelectedPackets(selectedProduct),
                'size',
                constants.SORT_TYPE.ALPHA_NUMERIC,
                constants.SORT_DIRECTION.DESCENDING
            ) as SelectedPacket[],
        [selectedProduct]
    )

    const alignCenterClassName =
        packetList.length === 1 ? 'align-add-on-center' : ''

    return (
        <div
            data-testid="packet-selection"
            className="packet-selection packet-selection--addon"
        >
            <div className="packet-selection--main">
                <div className="packet-selection--heading-addon">
                    <div className="packet-selection--heading-icon-addon">
                        {plusIcon({
                            color: colors.white,
                            width: '20',
                            height: '20'
                        })}
                    </div>
                    <h2 className="packet-selection--heading-title">
                        {t('enrollment.addOns')}
                    </h2>
                </div>
                <div>
                    <Container>
                        <Row className={alignCenterClassName}>
                            {selectedProduct &&
                                packetList.map((productPacket) => (
                                    <Col
                                        md={3}
                                        col={6}
                                        key={productPacket.package_id}
                                    >
                                        <div className="packet-selection--single-packet">
                                            <PacketAddOn
                                                productPacket={productPacket}
                                                quantity={
                                                    getPacketQuantity(
                                                        productPacket,
                                                        preSelectedPackets
                                                    ) || 0
                                                }
                                                packetSelectionHandler={
                                                    handlePacketSelection
                                                }
                                            />
                                        </div>
                                    </Col>
                                ))}
                        </Row>
                    </Container>
                </div>
            </div>
        </div>
    )
}

export const PacketSelection: FC<PacketSelectionInterface> = (props) => {
    const { isAddOn = false, ...baseProps } = props
    return isAddOn ? (
        <PacketSelectionAddOn {...baseProps} />
    ) : (
        <PacketSelectionProduct {...baseProps} />
    )
}
